import axios from 'axios';
import { authService } from '../services/authService';
import { getBaseURL, optimcrBase, workups_url } from '../utils/config';
import { toast } from '../common/toast';
import {
  getRequestFailureMessage,
  isLikelyUnreachableError,
  REDEPLOY_MESSAGE,
  SERVER_UNREACHABLE,
  UNREACHABLE_SERVER_TOAST_OPTIONS,
  UNREACHABLE_SERVICE_MESSAGE,
  showRequestFailureToast,
} from './requestErrors';
import { getSigninURLWithAllParams } from '../utils/urlUtils';
import logger from '../utils/logger';

const ML_SERVER_LABEL = `the ML server (${optimcrBase})`;

const enhanceUnreachableError = (error, { serviceLabel } = {}) => {
  const target = serviceLabel || error?.serviceLabel || 'the server';
  error.code = SERVER_UNREACHABLE;
  error.serviceLabel = target;
  error.userMessage = `Unable to reach ${target}. ${UNREACHABLE_SERVICE_MESSAGE}`;
  return error;
};

const attachAuthInterceptor = (instance) => {
  instance.interceptors.request.use(
    (config) => {
      if (!config) {
        return Promise.reject(new Error('Invalid request configuration'));
      }
      if (config.url?.includes('/signin')) {
        return config;
      }

      const sessionToken = authService.getActiveSessionToken();
      if (sessionToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${sessionToken}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );
};

const attachUnreachableErrorEnhancement = (instance, serviceLabel) => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isLikelyUnreachableError(error)) {
        enhanceUnreachableError(error, { serviceLabel });
      }
      return Promise.reject(error);
    }
  );
};

// This file handles all HTTP requests and automatically handles 401 Unauthorized errors
// by forcing the user to logout and redirecting to the signin page.
//
// Interceptors run outside the React tree and cannot call useNavigate(). On auth
// failure we dispatch `auth:redirect`; AuthRedirectHandler in App.js listens and
// renders <Navigate replace />. React components (e.g. UserContext) should use
// useStableNavigate() directly instead of this event.

// Track recent session errors to prevent duplicate toasts
const recentSessionErrors = new Map();
const SESSION_ERROR_COOLDOWN = 3000; // 3 seconds

// Helper function to check if we should show a session error toast
const shouldShowSessionErrorToast = (errorMessage) => {
  const now = Date.now();
  const lastShown = recentSessionErrors.get(errorMessage);
  
  if (!lastShown || (now - lastShown) > SESSION_ERROR_COOLDOWN) {
    recentSessionErrors.set(errorMessage, now);
    return true;
  }
  
  return false;
};

// Function to clear recent session errors (exposed globally)
const clearRecentSessionErrors = () => {
  recentSessionErrors.clear();
};

// Expose the function globally so it can be called from UserContext
window.clearRecentSessionErrors = clearRecentSessionErrors;

// Create Axios instance
const axiosInstance = axios.create({
  baseURL: getBaseURL(),
  timeout: 900000, // 900 seconds
  headers: {
    'Content-Type': 'application/json',
    // You may have other default headers here
  },
  referrerPolicy: {
    policy: 'strict-origin-when-cross-origin',
  },
});

// Create Axios instance without toast-displaying interceptors.
// Used by flows that want to aggregate their own errors into a single UI toast.
const axiosInstanceNoToast = axios.create({
  baseURL: getBaseURL(),
  timeout: 900000, // 900 seconds
  headers: {
    'Content-Type': 'application/json'
  },
  referrerPolicy: {
    policy: 'strict-origin-when-cross-origin',
  },
});

axiosInstanceNoToast.interceptors.request.use(
  (config) => {
    if (!config) {
      return Promise.reject(new Error('Invalid request configuration'));
    }
    if (config.url?.includes('/signin')) {
      return config;
    }

    const sessionToken = authService.getActiveSessionToken();
    if (sessionToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${sessionToken}`;
      return config;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

attachUnreachableErrorEnhancement(axiosInstanceNoToast, 'the server');

axiosInstanceNoToast.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status === 401) {
      // Trigger logout/redirect but do not toast for this service.
      await authService.logout();

      // auth:redirect — interceptors cannot use useNavigate (see file header)
      window.dispatchEvent(
        new CustomEvent('auth:redirect', {
          detail: { path: '/signin' }
        })
      );

      return Promise.reject(error);
    }

    const isAuthError =
      error.message === 'Invalid or expired session' ||
      error.message === 'No session found' ||
      error.message === 'Invalid session data' ||
      error?.response?.data?.error === 'Session Expired, please log back in' ||
      error?.response?.data?.error === 'No valid authorization header' ||
      error?.response?.data?.message === 'Could not validate credentials';

    if (isAuthError && window.location.pathname !== '/signin') {
      await authService.logout();
      const signinURL = getSigninURLWithAllParams();
      // auth:redirect — interceptors cannot use useNavigate (see file header)
      window.dispatchEvent(
        new CustomEvent('auth:redirect', {
          detail: { path: signinURL }
        })
      );
    }

    return Promise.reject(error);
  }
);



// Add request interceptor for auth validation -- you can also add versioning check here.
attachAuthInterceptor(axiosInstance);
attachUnreachableErrorEnhancement(axiosInstance, 'the server');

// Update response interceptor to handle errors without recursive auth checks
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Check for 401 Unauthorized errors first - these should always trigger logout
    if (error?.response?.status === 401) {
      logger.log('401 Unauthorized error detected, forcing logout');
      
      // Use authService logout which handles everything
      await authService.logout();
      
      // auth:redirect — interceptors cannot use useNavigate (see file header)
      window.dispatchEvent(new CustomEvent('auth:redirect', { 
        detail: { path: '/signin' } 
      }));
      
      // Don't show error toast for 401s since we're logging out anyway
      return Promise.reject(error);
    }

    // Protected stip-status cancels are toasted by CancelOffers/cancelOfferService.
    if (error?.response?.data?.protected_status) {
      return Promise.reject(error);
    }

    // Show a toast with backend error if available, otherwise generic message
    try {
      logger.error('Error:', error);
      const backendMessage = error?.response?.data?.message || error?.response?.data?.error?.message || error?.response?.data?.error;
      logger.error('Backend error message:', backendMessage);
      if (error?.response?.status === 404) {
        if (shouldShowSessionErrorToast(REDEPLOY_MESSAGE)) {
          toast.error(REDEPLOY_MESSAGE);
        }
      } else if (backendMessage) {
        toast.error(backendMessage);
      } else if (isLikelyUnreachableError(error)) {
        const message = getRequestFailureMessage(error, { serviceLabel: 'the server' });
        if (shouldShowSessionErrorToast(message)) {
          toast.error(message, UNREACHABLE_SERVER_TOAST_OPTIONS);
        }
      } else if (error.message && shouldShowSessionErrorToast(error.message)) {
        toast.error(error.message);
      }
    } catch (e) {
      // Failsafe – never block the promise chain because toast failed
      logger.error('Failed to display toast for error', e);
    }

    // Handle other auth-related errors
    const isAuthError = 
      error.message === 'Invalid or expired session' ||
      error.message === 'No session found' ||
      error.message === 'Invalid session data' ||
      error?.response?.data?.error === 'Session Expired, please log back in' ||
      error?.response?.data?.error === 'No valid authorization header' ||
      error?.response?.data?.message === 'Could not validate credentials';

    if (isAuthError && window.location.pathname !== '/signin') {
      logger.log('Auth error detected, forcing logout');
      
      // Use authService logout which handles everything
      await authService.logout();
      
      // Use smooth navigation instead of hard refresh
      // Dispatch a custom event that can be listened to by the app
      // Preserve all URL parameters when redirecting
      const signinURL = getSigninURLWithAllParams();
      // auth:redirect — interceptors cannot use useNavigate (see file header)
      window.dispatchEvent(new CustomEvent('auth:redirect', { 
        detail: { path: signinURL } 
      }));
    }
    
    return Promise.reject(error);
  }
);



const mlAxiosInstance = axios.create({
  baseURL: optimcrBase,
  timeout: 900000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const mlAxiosInstanceNoToast = axios.create({
  baseURL: optimcrBase,
  timeout: 900000,
  headers: {
    'Content-Type': 'application/json',
  },
});

attachAuthInterceptor(mlAxiosInstance);
attachAuthInterceptor(mlAxiosInstanceNoToast);
attachUnreachableErrorEnhancement(mlAxiosInstance, ML_SERVER_LABEL);
attachUnreachableErrorEnhancement(mlAxiosInstanceNoToast, ML_SERVER_LABEL);

mlAxiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status === 401) {
      await authService.logout();
      window.dispatchEvent(
        new CustomEvent('auth:redirect', {
          detail: { path: '/signin' },
        })
      );
      return Promise.reject(error);
    }

    try {
      if (error?.response?.status === 404) {
        if (shouldShowSessionErrorToast(REDEPLOY_MESSAGE)) {
          toast.error(REDEPLOY_MESSAGE);
        }
      } else if (isLikelyUnreachableError(error)) {
        const message = getRequestFailureMessage(error, { serviceLabel: ML_SERVER_LABEL });
        if (shouldShowSessionErrorToast(message)) {
          toast.error(message, UNREACHABLE_SERVER_TOAST_OPTIONS);
        }
      } else {
        const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
        if (backendMessage) {
          toast.error(backendMessage);
        } else if (error.message && shouldShowSessionErrorToast(error.message)) {
          toast.error(error.message);
        }
      }
    } catch (e) {
      logger.error('Failed to display toast for ML server error', e);
    }

    return Promise.reject(error);
  }
);



// Requests to workups_url will automatically retry once on failure with user notification
// Utility function for fetch requests with authorization headers
export const authorizedFetch = async (url, options = {}) => {
  const sessionToken = authService.getActiveSessionToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` }),
    ...options.headers,
  };

  // Check if this is a workups request that should be retried
  const isWorkupsRequest = url.includes(workups_url);
  
  const makeRequest = async (isRetry = false, retryOptions = {}) => {
    try {
      const response = await fetch(url, {
        ...(retryOptions && Object.keys(retryOptions).length > 0 ? retryOptions : options),
        headers,
      });

      // Handle 401 errors in fetch requests as well
      if (response.status === 401) {
        logger.log('401 Unauthorized error detected in fetch request, forcing logout');
        
        // Use authService logout which handles everything
        await authService.logout();
        
        // auth:redirect — interceptors cannot use useNavigate (see file header)
        const signinURL = getSigninURLWithAllParams();
        window.dispatchEvent(new CustomEvent('auth:redirect', { 
          detail: { path: signinURL } 
        }));
      }

      // If this was a successful retry, notify the user
      if (isWorkupsRequest && options._isRetry) {
        toast.success('Request completed successfully on retry!', {
          position: 'top-right',
          autoClose: 2000,
          closeOnClick: true,
          draggable: true,
        });
      }

      return response;
    } catch (error) {
      if (isLikelyUnreachableError(error)) {
        enhanceUnreachableError(error, { serviceLabel: ML_SERVER_LABEL });
      }

      // If this is a workups request and it's the first attempt, retry once
      if (isWorkupsRequest && !isRetry) {
        logger.log('Workups request failed, attempting retry...', error);
        
        // Determine the type of error for better user messaging
        let errorMessage = 'Connection issue detected. Retrying request automatically...';
        
        if (isLikelyUnreachableError(error)) {
          errorMessage = 'ML server may be temporarily unavailable. Retrying request automatically...';
        } else if (error.message && error.message.includes('timeout')) {
          errorMessage = 'Request timed out. Retrying automatically...';
        }
        
        // Notify user about the retry
        toast.info(errorMessage, {
          position: 'top-right',
          autoClose: 3000,
          closeOnClick: true,
          draggable: true,
        });
        
        // Wait a moment before retrying
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Retry the request with retry flag
        const retryOptions = { ...options, _isRetry: true };
        return makeRequest(true, retryOptions);
      }
      
      // If it's a retry attempt or not a workups request, throw the error
      throw error;
    }
  };

  return makeRequest();
};

export { axiosInstanceNoToast, mlAxiosInstance, mlAxiosInstanceNoToast };
export {
  getRequestFailureMessage,
  isLikelyUnreachableError,
  SERVER_UNREACHABLE,
  UNREACHABLE_SERVER_TOAST_OPTIONS,
  showRequestFailureToast,
} from './requestErrors';
export default axiosInstance;
